import { Row, Col, Typography, Space, Card, Tag, Spin } from "antd";
import {
  CheckCircleOutlined,
  BankOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../../hooks/reduxHooks";
import { requiredInfoSuggestions } from "../../../../data/requiredInfoSuggestions";
import {
  getBusinessInfoApi,
  getTenantToolsApi,
} from "../../../../services/tenantSetup.service";
import { useEffect, useState } from "react";

const { Title, Text, Paragraph } = Typography;

const Step3ReviewConfirmation = ({ setupData, onSubmit, loading }) => {
  const dispatch = useAppDispatch();
  const [businessInfo, setBusinessInfo] = useState(null);

  const [toolsList, setToolsList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Merge setupData (local form data) with businessInfo (API data), preferring setupData
  const step1Data = setupData?.step1 || {};
  const step2Data = setupData?.step2 || {};

  // Combined display data - use local setupData first, then fallback to API data
  const displayData = {
    // Organization data (from step1)
    org_name: step1Data.org_name || businessInfo?.org_name || "Not provided",
    contact_number: step1Data.org_contact || businessInfo?.contact_number || "Not provided",
    org_email: step1Data.org_email || businessInfo?.org_email || "Not provided",
    org_description: step1Data.org_description || businessInfo?.org_description || "Not provided",
    // Agent data (from step2)
    agent_name: step2Data.agent_name || businessInfo?.agent_name || "AI Bot",
    language_to_reply: step2Data.language || businessInfo?.language_to_reply || ["Auto Detect"],
    romanize_reply: step2Data.romanize_reply || businessInfo?.romanize_reply || false,
    agent_goal_type: step2Data.agent_goal_type || businessInfo?.agent_goal_type || "",
    additional_agent_goal: step2Data.additional_agent_goal || businessInfo?.additional_agent_goal || "",
    response_style: step2Data.prompt || businessInfo?.response_style || "standard",
    selected_tools: step2Data.intervention_tools || businessInfo?.selected_tools || [],
    selected_tools_required_info: step2Data.selected_tools_required_info || businessInfo?.selected_tools_required_info || [],
  };



  const getToolLabel = (value) => {
    // Find the tool in the API data - supports grouped (tools/booking) or flat arrays
    let tool = toolsList?.tools?.find(t => (t.issue_type || t.name) === value);
    if (!tool) {
      tool = toolsList?.booking?.find(t => (t.issue_type || t.name) === value);
    }
    if (!tool && Array.isArray(toolsList)) {
      tool = toolsList.find(t => (t.issue_type || t.name) === value);
    }
    return tool ? tool.label : value;
  };

  // Map required info name -> label
  const requiredInfoLabelMap = new Map((requiredInfoSuggestions || []).map(s => [s.name, s.label]));

  const getResponseStyleLabel = (style) => {
    const styles = {
      simplified: "Simplified",
      standard: "Standard",
      elaborated: "Elaborated",
    };
    return styles[style] || style;
  };

  const getResponseStyleDescription = (style) => {
    const descriptions = {
      simplified: "Quick, concise answers",
      standard: "Comprehensive explanations",
      elaborated: "In-depth technical details with step-by-step guidance",
    };
    return descriptions[style] || style || "AI response style";
  };

  const getAgentGoalSummary = (goalType) => {
    return goalType;
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        await Promise.all([
          new Promise((resolve) => {
            getTenantToolsApi({
              finalCallback: resolve,
              successCallback: (response) => {
                setToolsList(response);
              },
              failureCallback: resolve,
            })();
          }),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    getBusinessInfoApi({
      finalCallback: () => { },
      successCallback: (response) => {
        setBusinessInfo(response);
      },
      failureCallback: () => { },
    })();
  }, []);

  const handleConfirmSetup = () => {
    onSubmit();
  };

  return (
    <div className="step3-review-confirmation">
      <div
        className="step3-review-confirmation__header"
        style={{ textAlign: "center", marginBottom: 40 }}
      >
        <div
          style={{
            background: "#52c41a",
            borderRadius: "50%",
            width: 80,
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px auto",
          }}
        >
          <CheckCircleOutlined
            style={{
              color: "white",
              fontSize: 40,
            }}
          />
        </div>

        <Title level={2} style={{ color: "#262626", marginBottom: 12 }}>
          Review & Confirm Setup
        </Title>

        <Paragraph
          style={{
            fontSize: "16px",
            color: "#595959",
            marginBottom: 0,
            maxWidth: 500,
            margin: "0 auto",
          }}
        >
          Review your configuration and confirm to complete the setup.
        </Paragraph>
      </div>

      {dataLoading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading your setup information...</Text>
          </div>
        </div>
      ) : (
        <>
          {/* Setup Summary */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            {/* Organization Summary */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <BankOutlined style={{ color: "#595959", fontSize: 16 }} />
                    <Text strong style={{ fontSize: 16 }}>
                      Organization Summary
                    </Text>
                  </Space>
                }
                className="summary-card"
                style={{
                  height: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ padding: "20px" }}>
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <SummaryItem
                      label="Organization Name"
                      value={displayData.org_name}
                    />


                    <SummaryItem
                      label="Contact Number"
                      value={displayData.contact_number}
                    />

                    <SummaryItem
                      label="Email Address"
                      value={displayData.org_email}
                    />

                    <SummaryItem
                      label="Description"
                      value={displayData.org_description}
                      isLast={true}
                    />
                  </Space>
                </div>
              </Card>
            </Col>

            {/* Agent Summary */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <RobotOutlined style={{ color: "#595959", fontSize: 16 }} />
                    <Text strong style={{ fontSize: 16 }}>
                      Agent Summary
                    </Text>
                  </Space>
                }
                className="summary-card"
                style={{
                  height: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ padding: "20px" }}>
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <SummaryItem
                      label="Agent Name"
                      value={displayData.agent_name}
                    />

                    <SummaryItem
                      label="Language to Reply In"
                      value={
                        <Space wrap>
                          {displayData.language_to_reply?.length > 0 ? (
                            displayData.language_to_reply.map((lang, index) => (
                              <Tag
                                key={lang}
                                color={index === 0 && lang !== "Auto Detect" ? 'blue' : 'default'}
                                style={{
                                  borderRadius: 6,
                                  fontSize: 13,
                                  padding: "1px 8px",
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                {lang}
                                {index === 0 && lang !== "Auto Detect" && (
                                  <span style={{ fontSize: 11, opacity: 0.8 }}>
                                    (Primary)
                                  </span>
                                )}
                              </Tag>
                            ))
                          ) : (
                            <Text style={{ color: "#8c8c8c" }}>
                              Not selected
                            </Text>
                          )}
                        </Space>
                      }
                    />

                    {/* <SummaryItem
                      label="Romanize Reply"
                      value={
                        displayData.romanize_reply ? (
                          <Tag
                            color="green"
                            style={{
                              borderRadius: 6,
                              fontSize: 13,
                              padding: "1px 8px",
                            }}
                          >
                            Enabled
                          </Tag>
                        ) : (
                          <Tag
                            style={{
                              borderRadius: 6,
                              fontSize: 13,
                              padding: "1px 8px",
                              border: "1px solid #d9d9d9",
                              background: "#fafafa",
                            }}
                          >
                            Disabled
                          </Tag>
                        )
                      }
                    /> */}

                    <SummaryItem
                      label="Agent Goal"
                      value={
                        displayData.agent_goal_type ? (
                          <Text
                            style={{
                              fontSize: 14,
                              lineHeight: 1.6,
                              color: "#595959",
                              display: "block",
                            }}
                          >
                            {getAgentGoalSummary(
                              displayData.agent_goal_type
                            )}
                          </Text>
                        ) : (
                          <Text style={{ color: "#8c8c8c" }}>
                            Not specified
                          </Text>
                        )
                      }
                    />

                    {displayData.additional_agent_goal && (
                      <SummaryItem
                        label="Additional Instructions"
                        value={displayData.additional_agent_goal}
                        isLast={true}
                      />
                    )}
                  </Space>
                </div>
              </Card>
            </Col>

            {/* Tools Summary */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <ThunderboltOutlined
                      style={{ color: "#595959", fontSize: 16 }}
                    />
                    <Text strong style={{ fontSize: 16 }}>Intervention Tools</Text>
                  </Space>
                }
                className="summary-card"
                style={{
                  height: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ padding: "20px" }}>
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <SummaryItem
                      label="Selected Tools"
                      value={
                        <Space wrap style={{ marginBottom: 12 }}>
                          {(displayData.selected_tools || []).map((toolName) => (
                            <Tag
                              key={toolName}
                              style={{
                                borderRadius: 6,
                                border: "1px solid #d9d9d9",
                                background: "#fafafa",
                                marginBottom: 6,
                                fontSize: 13,
                                padding: "1px 8px",
                              }}
                            >
                              {getToolLabel(toolName)}
                            </Tag>
                          ))}
                          {(!displayData.selected_tools || displayData.selected_tools.length === 0) && (
                            <Text style={{ color: "#8c8c8c" }}>None selected</Text>
                          )}
                        </Space>
                      }
                    />

                    {(displayData.selected_tools_required_info || []).map((toolInfo, toolIndex) => (
                      <SummaryItem
                        key={toolInfo.tool}
                        label={`${getToolLabel(toolInfo.tool)} Configuration`}
                        value={
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Required Information:</Text>
                              {toolInfo.required_info.length > 0 ? (
                                toolInfo.required_info.map((info, index) => (
                                  <Tag
                                    key={index}
                                    style={{
                                      borderRadius: 6,
                                      border: "1px solid #d9d9d9",
                                      background: "#fafafa",
                                      marginBottom: 4,
                                      marginRight: 4,
                                      fontSize: 12,
                                      padding: "2px 6px",
                                    }}
                                  >
                                    {requiredInfoLabelMap.get(info) || info}
                                  </Tag>
                                ))
                              ) : (
                                <Text size="small" type="secondary">None</Text>
                              )}
                            </div>
                            
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: toolInfo.allow_empty_response ? 0 : 8 }}>
                              <Tag color={toolInfo.allow_empty_response ? "success" : "default"} style={{ borderRadius: 4 }}>
                                {toolInfo.allow_empty_response ? "Allows Empty Response" : "Requires Reply Message"}
                              </Tag>
                              <Tag color={toolInfo.block_reply_until_resolved ? "processing" : "default"} style={{ borderRadius: 4 }}>
                                {toolInfo.block_reply_until_resolved ? "Blocks Reply Until Resolved" : "No Reply Block"}
                              </Tag>
                            </div>

                            {!toolInfo.allow_empty_response && (
                              <div style={{ 
                                padding: '8px 12px', 
                                background: '#f5f5f5', 
                                borderRadius: 6, 
                                borderLeft: '3px solid #d9d9d9',
                                marginTop: 4
                              }}>
                                <Text strong style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 2 }}>Reply Message:</Text>
                                <Text style={{ fontSize: 13, color: '#595959' }}>{toolInfo.reply_message}</Text>
                              </div>
                            )}
                          </div>
                        }
                        isLast={toolIndex === (displayData.selected_tools_required_info || []).length - 1}
                      />
                    ))}

                    {(!displayData.selected_tools_required_info || displayData.selected_tools_required_info.length === 0) && (
                      <SummaryItem
                        label="Required Information"
                        value={<Text style={{ color: "#8c8c8c" }}>No requirements configured</Text>}
                        isLast={true}
                      />
                    )}
                  </Space>
                </div>
              </Card>
            </Col>

            {/* AI Behavior Summary */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <BulbOutlined style={{ color: "#595959", fontSize: 16 }} />
                    <Text strong style={{ fontSize: 16 }}>
                      AI Behavior Summary
                    </Text>
                  </Space>
                }
                className="summary-card"
                style={{
                  height: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ padding: "20px" }}>
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <SummaryItem
                      label="Response Style"
                      value={
                        <div>
                          <Tag
                            style={{
                              borderRadius: 6,
                              border: "1px solid #d9d9d9",
                              background: "#fafafa",
                              fontSize: 13,
                              padding: "1px 8px",
                              marginBottom: 8,
                            }}
                          >
                            {displayData.response_style || "Not set"}
                          </Tag>
                          <Paragraph
                            style={{
                              fontSize: 13,
                              color: "#8c8c8c",
                              marginBottom: 0,
                              lineHeight: 1.4,
                            }}
                          >
                            {getResponseStyleDescription(displayData.response_style)}
                          </Paragraph>
                        </div>
                      }
                      isLast={true}
                    />
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Confirm Setup Section */}
          <div style={{
            textAlign: "center",
            marginTop: 40,
            padding: "32px 24px",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
            border: "1px solid #f0f0f0"
          }}>
            <Title level={4} style={{ marginBottom: 16 }}>
              Confirm Setup
            </Title>
            <Paragraph style={{ color: "#8c8c8c", marginBottom: 0 }}>
              Please review all the information above. Once you confirm, your AI assistant will be ready to launch.
            </Paragraph>
          </div>
        </>
      )}
    </div>
  );
};

// Reusable Summary Item Component
const SummaryItem = ({ label, value, isLast = false }) => (
  <div
    style={{
      marginBottom: isLast ? 0 : 10,
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
    }}
  >
    <div
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "#262626",
        marginTop: 6,
        flexShrink: 0,
      }}
    />
    <div style={{ flex: 1 }}>
      <Text
        strong
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#262626",
          display: "block",
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text style={{ fontSize: 14, color: "#595959" }}>
          {value}
        </Text>
      ) : (
        value
      )}
    </div>
  </div>
);

export default Step3ReviewConfirmation;
